// src/app/pages/ValidationPaymentPage/index.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronRight, Loader2, Receipt, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import { isCheckoutLocationState } from "@/app/core/TokensPayment/checkOutComp/subscribe/shared/checkoutTypes";
import { PaymentsApi } from '@/app/data/profilPayment/api/PaymentsApi';
import { AppStore } from "@/app/shared/store/AppStore";
import type { ValidationViewState } from "./types";

interface ValidationPaymentFeatureProps {
  lifecycle?: any;
}

export function ValidationPaymentPage(_props: ValidationPaymentFeatureProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const paymentsApi = new PaymentsApi();
  const appStore = AppStore.getInstance();
  const checkoutPayload = useMemo(
    () => (isCheckoutLocationState(location.state) ? location.state : null),
    [location.state],
  );
  const [viewState, setViewState] = useState<ValidationViewState>("processing");
  const [paymentError, setPaymentError] = useState("");
  const [attempt, setAttempt] = useState(0);

  const checkoutPayloadRef = useRef(checkoutPayload);
  checkoutPayloadRef.current = checkoutPayload;

  const hasStarted = useRef(false);

  useEffect(() => {
    if (!checkoutPayloadRef.current) {
      navigate("/profile", { replace: true });
      return;
    }

    if (hasStarted.current) return;
    hasStarted.current = true;

    let isCancelled = false;
    const runCheckout = async () => {
      setViewState("processing");
      setPaymentError("");
      const payload = checkoutPayloadRef.current!;
      try {
        // Берём валюту из стейта (уже сохранена TopUpPageController)
        const currency = (payload as any).currency ?? 'XTR';
        
        // 1. Создать платёж
        const paymentResponse = await paymentsApi.createPayment({
          packageCode: payload.productCode,
          currency: currency,
          providerCode: 'TELEGRAM',
          promoCode: payload.promoCode,
        });
        if (isCancelled) return;

        // 2. Открыть invoice (если есть invoiceUrl)
        if (paymentResponse.invoiceUrl) {
          window.open(paymentResponse.invoiceUrl, '_blank');
        }

        // 3. Полить статус
        const status = await paymentsApi.pollPaymentStatus(paymentResponse.paymentId);

        if (isCancelled) return;

        if (status === 'PAID') {
          setViewState("success");
        } else {
          setViewState("error");
          setPaymentError(paymentsApi.getCheckoutErrorMessage(status));
        }
      } catch (error) {
        if (isCancelled) return;
        setPaymentError(error instanceof Error ? error.message : "Не удалось начать оплату");
        setViewState("error");
      }
    };
    void runCheckout();
    return () => {
      isCancelled = true;
      hasStarted.current = false;
    };
  }, [attempt, navigate]);

  if (!checkoutPayload) return null;

  if (viewState === "processing") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-sm text-center"
        >
          <div className="relative mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-[2rem] bg-accent/10">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 rounded-[2rem] border-2 border-dashed border-accent/30"
            />
            <Receipt className="h-10 w-10 shrink-0 animate-pulse text-accent" />
          </div>
          <h2 className="mb-3 text-2xl font-bold tracking-tight">Ожидание оплаты</h2>
          <div className="inline-flex items-center gap-3 rounded-full bg-muted/50 px-5 py-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin text-accent" />
            Проверка статуса...
          </div>
        </motion.div>
      </div>
    );
  }

  if (viewState === "success") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="w-full max-w-sm text-center"
        >
          <div className="relative mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-[2rem] bg-green-500/10">
            <Check className="h-12 w-12 shrink-0 text-green-500" />
          </div>
          <h2 className="mb-3 text-2xl font-bold tracking-tight">Оплата успешна!</h2>
          <button
            onClick={() => {
              appStore.schedulePendingModal({
                type: "subscription_purchased",
                productName: checkoutPayload.productName,
                features: checkoutPayload.features ?? [],
              });
              navigate("/profile", { replace: true });
            }}
            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-foreground py-4 font-medium text-background"
          >
            Вернуться в профиль
            <ChevronRight className="h-4 w-4" />
          </button>
        </motion.div>
      </div>
    );
  }

  if (viewState === "error") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="w-full max-w-sm text-center"
        >
          <div className="relative mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-[2rem] bg-destructive/10">
            <X className="h-12 w-12 shrink-0 text-destructive" />
          </div>
          <h2 className="mb-3 text-2xl font-bold tracking-tight">Оплата не прошла</h2>
          <p className="mb-10 text-[15px] leading-relaxed text-muted-foreground">{paymentError}</p>
          <div className="flex w-full flex-col gap-3">
            <button
              onClick={() => setAttempt((current) => current + 1)}
              className="w-full rounded-2xl bg-foreground py-4 font-medium text-background"
            >
              Попробовать снова
            </button>
            <button
              onClick={() => navigate("/profile")}
              className="w-full rounded-2xl bg-muted py-4 font-medium text-foreground"
            >
              Выбрать другой тариф
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return null;
}